import { useMemo } from 'react';
import { TourType } from '@/Components/Tour/Tour';
import { useLocalization } from '@/Hooks';
import { archiveSectionActions } from '@/Pages/OnlineArchive/modules/Archive/services';
import { useDispatch } from 'react-redux';

export function useOnlineArchiveDocuments() {
  const dispatch = useDispatch();

  const {
    tours: {
      Accounting: { ArchiveTour, ArchiveModalTour, PostingTour },
    },
  } = useLocalization();

  const pageTour = useMemo<TourType>(
    () => ({
      id: 'pageTour',
      title: ArchiveTour.Title,
      steps: [
        {
          content: ArchiveTour.Step1.Text,
          placement: 'auto',
          target: '#archive',
          title: ArchiveTour.Step1.Title,
          disableBeacon: true,
          link: ArchiveTour.Step1.DetailsLink,
        },
        {
          content: ArchiveTour.Step2.Text,
          placement: 'auto',
          target: '#documentFilter',
          title: ArchiveTour.Step2.Title,
          disableBeacon: true,
          link: ArchiveTour.Step2.DetailsLink,
        },
        {
          content: ArchiveTour.Step3.Text,
          placement: 'auto',
          target: '#archiveTable',
          title: ArchiveTour.Step3.Title,
          disableBeacon: true,
          link: ArchiveTour.Step3.DetailsLink,
        },
        {
          content: ArchiveTour.Step4.Text,
          placement: 'auto',
          target: '#documentCrud',
          title: ArchiveTour.Step4.Title,
          disableBeacon: true,
          link: ArchiveTour.Step4.DetailsLink,
        },
        {
          content: ArchiveTour.Step5.Text,
          placement: 'left',
          target: '.dx-toolbar-after',
          title: ArchiveTour.Step5.Title,
          disableBeacon: true,
          link: ArchiveTour.Step5.DetailsLink,
          spotlightPadding: 0,
          styles: {
            spotlight: {
              maxWidth: '500px',
            },
          },
        },
        {
          content: ArchiveTour.Step6.Text,
          placement: 'auto',
          target: '#tableImportButton',
          title: ArchiveTour.Step6.Title,
          disableBeacon: true,
          link: ArchiveTour.Step6.DetailsLink,
          styles: {
            spotlight: {
              minWidth: '65px',
            },
          },
        },
        {
          content: ArchiveTour.Step7.Text,
          placement: 'auto',
          target: '#documentInfo',
          title: ArchiveTour.Step7.Title,
          disableBeacon: true,
          link: ArchiveTour.Step7.DetailsLink,
        },
        {
          content: ArchiveTour.Step8.Text,
          placement: 'auto',
          target: '#showPostings',
          title: ArchiveTour.Step8.Title,
          disableBeacon: true,
          link: ArchiveTour.Step8.DetailsLink,
        },
        {
          content: ArchiveTour.Step9.Text,
          placement: 'top',
          target: '#archiveSettings',
          title: ArchiveTour.Step9.Title,
          disableBeacon: true,
          link: ArchiveTour.Step9.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '130px',
            },
          },
        },
        {
          content: ArchiveTour.Step10.Text,
          placement: 'auto',
          target: '#openPreviewDocument',
          title: ArchiveTour.Step10.Title,
          disableBeacon: true,
          link: ArchiveTour.Step10.DetailsLink,
        },
      ],
    }),
    [ArchiveTour],
  );

  const modalTour = useMemo<TourType>(
    () => ({
      id: 'modalTour',
      title: ArchiveModalTour.Title,
      before: () => {
        dispatch(archiveSectionActions.setNeedShowModal(true));
      },
      steps: [
        {
          content: ArchiveModalTour.Step1.Text,
          placement: 'auto',
          target: '#modalTitle',
          title: ArchiveModalTour.Step1.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step1.DetailsLink,
        },
        {
          content: ArchiveModalTour.Step2.Text,
          placement: 'top',
          target: '#operationInfo',
          title: ArchiveModalTour.Step2.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step2.DetailsLink,
          styles: {
            spotlight: {
              maxHeight: '35px',
            },
          },
        },
        {
          content: ArchiveModalTour.Step3.Text,
          placement: 'bottom',
          target: '#operationInfo',
          title: ArchiveModalTour.Step3.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step3.DetailsLink,
          styles: {
            spotlight: {
              marginTop: '30px',
              maxHeight: '120px',
            },
          },
        },
        {
          content: ArchiveModalTour.Step4.Text,
          placement: 'auto',
          target: '#operationNotes',
          title: ArchiveModalTour.Step4.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step4.DetailsLink,
        },
        {
          content: ArchiveModalTour.Step5.Text,
          placement: 'top',
          target: '#accountingInfo',
          title: ArchiveModalTour.Step5.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step5.DetailsLink,
        },
        {
          content: ArchiveModalTour.Step6.Text,
          placement: 'auto',
          target: '#generalInfo',
          title: ArchiveModalTour.Step6.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step6.DetailsLink,
        },
        {
          content: ArchiveModalTour.Step7.Text,
          placement: 'auto',
          target: '#contactInfo',
          title: ArchiveModalTour.Step7.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step7.DetailsLink,
        },
        {
          content: ArchiveModalTour.Step8.Text,
          placement: 'auto',
          target: '#archiveGeneralSettings',
          title: ArchiveModalTour.Step8.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step8.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '100px',
            },
          },
        },
        {
          content: ArchiveModalTour.Step9.Text,
          placement: 'left',
          target: '#openModalDocument',
          title: ArchiveModalTour.Step9.Title,
          disableBeacon: true,
          link: ArchiveModalTour.Step9.DetailsLink,
        },
      ],
    }),
    [ArchiveModalTour],
  );

  const postingTour = useMemo<TourType>(
    () => ({
      id: 'postingTour',
      title: PostingTour.Title,
      before: () => {
        dispatch(archiveSectionActions.setNeedShowModal(false));
        dispatch(archiveSectionActions.setNeedShowPostingModal(true));
      },
      after: () => {
        dispatch(archiveSectionActions.setNeedShowPostingModal(false));
      },
      steps: [
        {
          content: PostingTour.Step1.Text,
          placement: 'auto',
          target: '#postingTitle',
          title: PostingTour.Step1.Title,
          disableBeacon: true,
          link: PostingTour.Step1.DetailsLink,
        },
        {
          content: PostingTour.Step2.Text,
          placement: 'auto',
          target: '#amountType',
          title: PostingTour.Step2.Title,
          disableBeacon: true,
          link: PostingTour.Step2.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '60px',
              marginTop: '-25px',
            },
          },
        },
        {
          content: PostingTour.Step3.Text,
          placement: 'bottom',
          target: '#postingDate',
          title: PostingTour.Step3.Title,
          disableBeacon: true,
          link: PostingTour.Step3.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '60px',
              marginTop: '-25px',
            },
          },
        },
        {
          content: PostingTour.Step4.Text,
          placement: 'auto',
          target: '#paymentDate',
          title: PostingTour.Step4.Title,
          disableBeacon: true,
          link: PostingTour.Step4.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '60px',
              marginTop: '-25px',
            },
          },
        },
        {
          content: PostingTour.Step5.Text,
          placement: 'top',
          target: '#amount',
          title: PostingTour.Step5.Title,
          disableBeacon: true,
          link: PostingTour.Step5.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '60px',
              minWidth: '170px',
              marginTop: '-25px',
            },
          },
        },
        {
          content: PostingTour.Step6.Text,
          placement: 'auto',
          target: '#amountCurrency',
          title: PostingTour.Step6.Title,
          disableBeacon: true,
          link: PostingTour.Step6.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '60px',
              marginTop: '-25px',
            },
          },
        },
        {
          content: PostingTour.Step7.Text,
          placement: 'left',
          target: '#accountChart',
          title: PostingTour.Step7.Title,
          disableBeacon: true,
          link: PostingTour.Step7.DetailsLink,
          styles: {
            spotlight: {
              minHeight: '60px',
              marginTop: '-25px',
            },
          },
        },
        {
          content: PostingTour.Step8.Text,
          placement: 'auto',
          target: '#totalAmount',
          title: PostingTour.Step8.Title,
          disableBeacon: true,
          link: PostingTour.Step8.DetailsLink,
        },
        {
          content: PostingTour.Step9.Text,
          placement: 'left',
          target: '#openPostingPreview',
          title: PostingTour.Step9.Title,
          disableBeacon: true,
          link: PostingTour.Step9.DetailsLink,
        },
      ],
    }),
    [PostingTour],
  );

  return [pageTour, modalTour, postingTour];
}
